import { useState, useEffect } from 'react';
import { useLoanApplication } from '../../../contexts/LoanApplicationContext';

interface Step2LoanDetailsProps {
  onNext: () => void;
  onBack: () => void;
}

interface LoanType {
  id: string;
  name: string;
  description: string;
  minAmount: number;
  maxAmount: number;
  minTerm: number;
  maxTerm: number;
  interestRate: number;
  icon: string;
}

const loanTypes: LoanType[] = [
  {
    id: 'salary-based',
    name: 'Salary-Based Loan',
    description: 'For salaried employees with regular income',
    minAmount: 100,
    maxAmount: 5000,
    minTerm: 1,
    maxTerm: 24,
    interestRate: 18,
    icon: 'ri-money-dollar-circle-line'
  },
  {
    id: 'sme-working-capital',
    name: 'SME & Working Capital',
    description: 'For registered businesses and working capital needs',
    minAmount: 500,
    maxAmount: 10000,
    minTerm: 3,
    maxTerm: 36,
    interestRate: 22,
    icon: 'ri-store-2-line'
  },
  {
    id: 'microenterprise',
    name: 'Microenterprise Loan',
    description: 'For small businesses and informal traders',
    minAmount: 50,
    maxAmount: 2000,
    minTerm: 1,
    maxTerm: 12,
    interestRate: 20,
    icon: 'ri-shopping-bag-line'
  },
  {
    id: 'asset-backed',
    name: 'Asset-Backed Loan',
    description: 'Secured loans against valuable assets',
    minAmount: 200,
    maxAmount: 15000,
    minTerm: 3,
    maxTerm: 48,
    interestRate: 16,
    icon: 'ri-car-line'
  }
];

const loanPurposes = [
  'Business Expansion',
  'Working Capital',
  'Equipment Purchase',
  'Inventory/Stock',
  'Education Fees',
  'Medical Expenses',
  'Home Improvement',
  'Debt Consolidation',
  'Vehicle Purchase',
  'Emergency Expenses',
  'Other'
];

export default function Step2LoanDetails({ onNext, onBack }: Step2LoanDetailsProps) {
  const { applicationData, updateLoanDetails, setCurrentStep } = useLoanApplication();
  
  const [selectedLoanType, setSelectedLoanType] = useState<string>(applicationData.loanDetails.loanType || '');
  const [loanAmount, setLoanAmount] = useState<string>(applicationData.loanDetails.loanAmount?.toString() || '');
  const [loanTerm, setLoanTerm] = useState<string>(applicationData.loanDetails.loanTerm?.toString() || '');
  const [loanPurpose, setLoanPurpose] = useState<string>(applicationData.loanDetails.loanPurpose || '');
  const [otherPurpose, setOtherPurpose] = useState<string>(applicationData.loanDetails.otherPurpose || '');
  
  // SME specific fields
  const [businessRegNumber, setBusinessRegNumber] = useState<string>(applicationData.loanDetails.businessRegNumber || '');
  const [yearsInOperation, setYearsInOperation] = useState<string>(applicationData.loanDetails.yearsInOperation || '');
  
  // Asset-backed specific fields
  const [assetDescription, setAssetDescription] = useState<string>(applicationData.loanDetails.assetDescription || '');
  const [assetValue, setAssetValue] = useState<string>(applicationData.loanDetails.assetValue || '');
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [totalInterest, setTotalInterest] = useState<number>(0);
  const [totalRepayment, setTotalRepayment] = useState<number>(0);

  const selectedLoan = loanTypes.find(lt => lt.id === selectedLoanType);

  // Calculate loan estimates
  useEffect(() => {
    if (selectedLoan && loanAmount && loanTerm) {
      const principal = parseFloat(loanAmount);
      const months = parseInt(loanTerm);
      const monthlyRate = selectedLoan.interestRate / 100 / 12;
      
      if (principal > 0 && months > 0) {
        // Calculate monthly payment using amortization formula
        const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, months)) / 
                       (Math.pow(1 + monthlyRate, months) - 1);
        
        const total = payment * months;
        const interest = total - principal;
        
        setMonthlyPayment(payment);
        setTotalInterest(interest);
        setTotalRepayment(total);
      }
    }
  }, [loanAmount, loanTerm, selectedLoan]);

  // Auto-save
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedLoanType) {
        updateLoanDetails({
          loanType: selectedLoanType,
          loanAmount: loanAmount ? parseFloat(loanAmount) : undefined,
          loanTerm: loanTerm ? parseInt(loanTerm) : undefined,
          loanPurpose,
          otherPurpose,
          businessRegNumber,
          yearsInOperation,
          assetDescription,
          assetValue
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedLoanType, loanAmount, loanTerm, loanPurpose, otherPurpose, businessRegNumber, yearsInOperation, assetDescription, assetValue, updateLoanDetails]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!selectedLoanType) {
      newErrors.loanType = 'Please select a loan type';
    }

    if (!loanAmount) {
      newErrors.loanAmount = 'Loan amount is required';
    } else if (selectedLoan) {
      const amount = parseFloat(loanAmount);
      if (amount < selectedLoan.minAmount) {
        newErrors.loanAmount = `Minimum amount is $${selectedLoan.minAmount}`;
      } else if (amount > selectedLoan.maxAmount) {
        newErrors.loanAmount = `Maximum amount is $${selectedLoan.maxAmount}`;
      }
    }

    if (!loanTerm) {
      newErrors.loanTerm = 'Loan term is required';
    } else if (selectedLoan) {
      const term = parseInt(loanTerm);
      if (term < selectedLoan.minTerm) {
        newErrors.loanTerm = `Minimum term is ${selectedLoan.minTerm} months`;
      } else if (term > selectedLoan.maxTerm) {
        newErrors.loanTerm = `Maximum term is ${selectedLoan.maxTerm} months`;
      }
    }

    if (!loanPurpose) {
      newErrors.loanPurpose = 'Please select the purpose of the loan';
    }

    if (loanPurpose === 'Other' && !otherPurpose.trim()) {
      newErrors.otherPurpose = 'Please specify the purpose';
    }

    // SME specific validation
    if (selectedLoanType === 'sme-working-capital') {
      if (!businessRegNumber.trim()) {
        newErrors.businessRegNumber = 'Business registration number is required';
      }
      if (!yearsInOperation) {
        newErrors.yearsInOperation = 'Years in operation is required';
      }
    }

    // Asset-backed specific validation
    if (selectedLoanType === 'asset-backed') {
      if (!assetDescription.trim()) {
        newErrors.assetDescription = 'Asset description is required';
      }
      if (!assetValue) {
        newErrors.assetValue = 'Estimated asset value is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      updateLoanDetails({
        loanType: selectedLoanType,
        loanAmount: parseFloat(loanAmount),
        loanTerm: parseInt(loanTerm),
        loanPurpose,
        otherPurpose,
        businessRegNumber,
        yearsInOperation,
        assetDescription,
        assetValue
      });
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLoanTypeSelect = (loanTypeId: string) => {
    setSelectedLoanType(loanTypeId);
    setErrors({});
    
    // Reset amount and term if they're outside the new loan type's limits
    const newLoanType = loanTypes.find(lt => lt.id === loanTypeId);
    if (newLoanType) {
      const currentAmount = parseFloat(loanAmount);
      if (currentAmount && (currentAmount < newLoanType.minAmount || currentAmount > newLoanType.maxAmount)) {
        setLoanAmount('');
      }
      
      const currentTerm = parseInt(loanTerm);
      if (currentTerm && (currentTerm < newLoanType.minTerm || currentTerm > newLoanType.maxTerm)) {
        setLoanTerm('');
      }
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loan Details</h2>
          <p className="text-sm text-gray-600">Select your loan type and specify your requirements</p>
        </div>

        {/* Loan Type Selection */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-900 mb-4">
            Select Loan Type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loanTypes.map((loanType) => (
              <button
                key={loanType.id}
                type="button"
                onClick={() => handleLoanTypeSelect(loanType.id)}
                className={`p-5 rounded-lg border-2 text-left transition-all whitespace-nowrap cursor-pointer ${
                  selectedLoanType === loanType.id
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-teal-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-3">
                  <i className={`${loanType.icon} text-2xl text-teal-600 mt-1`}></i>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm">{loanType.name}</h3>
                    <p className="text-xs text-gray-600 mb-2">{loanType.description}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="text-gray-700">
                        ${loanType.minAmount} - ${loanType.maxAmount.toLocaleString()}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-700">
                        {loanType.minTerm}-{loanType.maxTerm} months
                      </span>
                    </div>
                  </div>
                  {selectedLoanType === loanType.id && (
                    <i className="ri-checkbox-circle-fill text-xl text-teal-600"></i>
                  )}
                </div>
              </button>
            ))}
          </div>
          {errors.loanType && (
            <p className="mt-2 text-sm text-red-600">{errors.loanType}</p>
          )}
        </div>

        {selectedLoan && (
          <>
            {/* Interest Rate Display */}
            <div className="mb-8 p-4 bg-teal-50 rounded-lg border border-teal-100">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-information-line text-teal-600"></i>
                <span className="text-sm font-semibold text-gray-900">Interest Rate</span>
              </div>
              <p className="text-2xl font-bold text-teal-600">{selectedLoan.interestRate}% per annum</p>
              <p className="text-xs text-gray-600 mt-1">Reducing balance method</p>
            </div>

            {/* Loan Amount */}
            <div className="mb-6">
              <label htmlFor="loanAmount" className="block text-sm font-semibold text-gray-900 mb-2">
                Loan Amount (USD) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="number"
                  id="loanAmount"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(e.target.value)}
                  min={selectedLoan.minAmount}
                  max={selectedLoan.maxAmount}
                  step="50"
                  className={`w-full pl-8 pr-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    errors.loanAmount ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={`${selectedLoan.minAmount} - ${selectedLoan.maxAmount.toLocaleString()}`}
                />
              </div>
              <p className="mt-1 text-xs text-gray-600">
                Min: ${selectedLoan.minAmount} | Max: ${selectedLoan.maxAmount.toLocaleString()}
              </p>
              {errors.loanAmount && (
                <p className="mt-2 text-sm text-red-600">{errors.loanAmount}</p>
              )}
            </div>

            {/* Loan Term */}
            <div className="mb-6">
              <label htmlFor="loanTerm" className="block text-sm font-semibold text-gray-900 mb-2">
                Loan Term (Months) <span className="text-red-500">*</span>
              </label>
              <select
                id="loanTerm"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.loanTerm ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select loan term</option>
                {Array.from(
                  { length: selectedLoan.maxTerm - selectedLoan.minTerm + 1 },
                  (_, i) => selectedLoan.minTerm + i
                ).map((month) => (
                  <option key={month} value={month}>
                    {month} {month === 1 ? 'month' : 'months'}
                  </option>
                ))}
              </select>
              {errors.loanTerm && (
                <p className="mt-2 text-sm text-red-600">{errors.loanTerm}</p>
              )}
            </div>

            {/* Loan Purpose */}
            <div className="mb-6">
              <label htmlFor="loanPurpose" className="block text-sm font-semibold text-gray-900 mb-2">
                Purpose of Loan <span className="text-red-500">*</span>
              </label>
              <select
                id="loanPurpose"
                value={loanPurpose}
                onChange={(e) => setLoanPurpose(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                  errors.loanPurpose ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select purpose</option>
                {loanPurposes.map((purpose) => (
                  <option key={purpose} value={purpose}>
                    {purpose}
                  </option>
                ))}
              </select>
              {errors.loanPurpose && (
                <p className="mt-2 text-sm text-red-600">{errors.loanPurpose}</p>
              )}
            </div>

            {/* Other Purpose */}
            {loanPurpose === 'Other' && (
              <div className="mb-6">
                <label htmlFor="otherPurpose" className="block text-sm font-semibold text-gray-900 mb-2">
                  Please Specify <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="otherPurpose"
                  value={otherPurpose}
                  onChange={(e) => setOtherPurpose(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                    errors.otherPurpose ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Describe the purpose of your loan"
                  maxLength={200}
                />
                {errors.otherPurpose && (
                  <p className="mt-2 text-sm text-red-600">{errors.otherPurpose}</p>
                )}
              </div>
            )}

            {/* SME Specific Fields */}
            {selectedLoanType === 'sme-working-capital' && (
              <>
                <div className="mb-6">
                  <label htmlFor="businessRegNumber" className="block text-sm font-semibold text-gray-900 mb-2">
                    Business Registration Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="businessRegNumber"
                    value={businessRegNumber}
                    onChange={(e) => setBusinessRegNumber(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.businessRegNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your business registration number"
                  />
                  {errors.businessRegNumber && (
                    <p className="mt-2 text-sm text-red-600">{errors.businessRegNumber}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label htmlFor="yearsInOperation" className="block text-sm font-semibold text-gray-900 mb-2">
                    Years in Operation <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="yearsInOperation"
                    value={yearsInOperation}
                    onChange={(e) => setYearsInOperation(e.target.value)}
                    className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.yearsInOperation ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select years</option>
                    <option value="less-than-1">Less than 1 year</option>
                    <option value="1-2">1-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="6-10">6-10 years</option>
                    <option value="more-than-10">More than 10 years</option>
                  </select>
                  {errors.yearsInOperation && (
                    <p className="mt-2 text-sm text-red-600">{errors.yearsInOperation}</p>
                  )}
                </div>
              </>
            )}

            {/* Asset-Backed Specific Fields */}
            {selectedLoanType === 'asset-backed' && (
              <>
                <div className="mb-6">
                  <label htmlFor="assetDescription" className="block text-sm font-semibold text-gray-900 mb-2">
                    Asset Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="assetDescription"
                    value={assetDescription}
                    onChange={(e) => setAssetDescription(e.target.value)}
                    rows={3}
                    className={`w-full px-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                      errors.assetDescription ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe the asset you're using as collateral (e.g., Vehicle make/model/year, Property details, Equipment specifications)"
                    maxLength={500}
                  />
                  {errors.assetDescription && (
                    <p className="mt-2 text-sm text-red-600">{errors.assetDescription}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label htmlFor="assetValue" className="block text-sm font-semibold text-gray-900 mb-2">
                    Estimated Asset Value (USD) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                    <input
                      type="number"
                      id="assetValue"
                      value={assetValue}
                      onChange={(e) => setAssetValue(e.target.value)}
                      min="0"
                      step="100"
                      className={`w-full pl-8 pr-4 py-3 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
                        errors.assetValue ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter estimated value"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    Professional valuation may be required during assessment
                  </p>
                  {errors.assetValue && (
                    <p className="mt-2 text-sm text-red-600">{errors.assetValue}</p>
                  )}
                </div>
              </>
            )}

            {/* Loan Estimate Calculator */}
            {loanAmount && loanTerm && monthlyPayment > 0 && (
              <div className="mb-8 p-6 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-100">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-calculator-line text-xl text-teal-600"></i>
                  <h3 className="text-base font-bold text-gray-900">Loan Estimate</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Monthly Payment</p>
                    <p className="text-2xl font-bold text-teal-600">
                      ${monthlyPayment.toFixed(2)}
                    </p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Total Interest</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${totalInterest.toFixed(2)}
                    </p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Total Repayment</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${totalRepayment.toFixed(2)}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-gray-600 mt-4 flex items-start gap-2">
                  <i className="ri-information-line text-sm mt-0.5"></i>
                  <span>This is an estimate based on the information provided. Final terms will be confirmed upon approval and may vary based on credit assessment.</span>
                </p>
              </div>
            )}
          </>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back
          </button>
          
          <button
            type="button"
            onClick={handleNext}
            className="px-6 py-3 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Continue to Documents
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </div>

      {/* RBZ Compliance Notice */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-start gap-3">
          <i className="ri-shield-check-line text-lg text-teal-600 mt-0.5"></i>
          <div>
            <p className="text-xs font-semibold text-gray-900 mb-1">RBZ Regulated Institution</p>
            <p className="text-xs text-gray-600">
              All loan products are subject to Reserve Bank of Zimbabwe regulations and responsible lending principles. 
              Interest rates and terms comply with RBZ guidelines for microfinance institutions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
